import React, { useState, useMemo, useRef } from 'react'
import { useQuery } from 'jsonapi-react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Layout } from 'app/theme/layout'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import StaticGoogleMap from 'app/maps/StaticGoogleMap'
import ErrorPage from 'app/pages/ErrorPage'
import NotFoundPage from 'app/pages/NotFoundPage'
import { useModalState } from 'lib/modal'
import LeadModal from 'app/leads/LeadModal'
import BookNextEvent from 'app/theme/banners/BookNextEvent'
import Slider from 'app/theme/slider'
import { addDays, format } from 'date-fns'
import { Calendar } from 'react-feather'
import styles from './LandmarkPage.module.css'

export default function LandmarkPage() {
  const params = useParams()

  const { openModal, ...modalState } = useModalState()

  const query = useQuery(['landmarks', {
    filter: {
      slug: params.landmarkSlug,
    },
    fields: {
      landmarks: ['id', 'name', 'slug', 'address'],
      city: ['name', 'url_slug']
    },
    include: [
      'city',
      'city.region',
      'city.parent',
    ],
    page: {
      limit: 1,
    },
  }])

  if (query.isLoading) {
    return null
  }

  if (query.error || !query.data) {
    return <ErrorPage />
  }

  if (!query.data.length) {
    return <NotFoundPage />
  }

  const landmark = query.data[0]
  const city = landmark.city

  if (!city || city.url_slug !== params.placeSlug) {
    return <NotFoundPage />
  }

  const crumbs = getCrumbs(landmark, city)

  let metaTitle
  let metaDescription
  let metaKeywords
  let googleMetaTags

  return (
    <Layout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription}></meta>
        <meta name="keywords" content={metaKeywords}></meta>
        <meta name="og:title" content={metaTitle}></meta>
        <script type="application/ld+json">{googleMetaTags}</script>
      </Helmet>
      <div className={[styles.container]}>
        <div className="absolute inset-0 flex items-center justify-center bg-gray-300">
          <StaticGoogleMap
            lat={landmark.address.location.lat}
            lng={landmark.address.location.lng}
            width={1160}
            height={360}
            zoom={13}
            className="absolute inset-0 border"
          />
        </div>
      </div>
      <div className="bg-white pt-10">
        <div className="container pb-10">
          <Breadcrumbs
            crumbs={crumbs}
          />
          <div className="mt-10">
            <div className="mt-4 flex items-center">
              <h1 className="text-3xl lg:text-4xl text-black font-platform leading-none">
                {landmark.name}
              </h1>
            </div>
            <a
              className="inline-block my-4 leading-tight text-xl text-blue border-b border-blue"
              target="_blank"
              href={`https://www.google.com/maps/search/?api=1&query=${landmark.address.formatted}`}
            >
              {landmark.address.formatted}
            </a>
            <p>Find below the food trucks that will be at {landmark.name} for the next 7 days.</p>
          </div>
        </div>
        <EventList landmark={landmark} />
      </div>
      <BookNextEvent
        onButtonClick={openModal}
      />
      <LeadModal
        {...modalState}
      />
    </Layout>
  )
}

function EventList(props) {
  const {
    landmark,
  } = props

  const [day, setDay] = useState(null)
  const sliderRef = useRef(null)
  const [sliderWidth, setSliderWidth] = useState(0)

  const dateRange = useMemo(() => {
    return [new Date(), addDays(new Date(), 31)]
  }, [])

  const query = useQuery(['calendar_events', {
    expand_recurring: true,
    private_events: false,
    point: [
      landmark.address?.location?.lng,
      landmark.address?.location?.lat,
    ].join(' '),
    filter: {
      lapsed: false,
      min_date: format(dateRange[0], 'yyyy-MM-dd'),
      max_date: format(dateRange[1], 'yyyy-MM-dd'),
      distance: {
        lte: .5,
      }
    },
    include: [
      'caterer',
      'caterer.photo',
      'caterer.tags',
    ]
  }])

  const days = useMemo(() => {
    const groups = {}
    const data = query.data || []

    data.forEach(event => {
      const date = format(event.start_date, 'yyyy-MM-dd')

      if (!groups[date]) {
        groups[date] = {
          title: format(event.start_date, 'EEEE, MMMM d'),
          date: event.start_date,
          label: {
            date: format(event.start_date, 'M/d'),
            day: format(event.start_date, 'E')
          },
          events: []
        }
      }

      groups[date].events.push(event)
    })

    const daysWithEvents = Object.values(groups)

    // create an array of the next 30 days
    const next28Days = []
    const today = new Date()
    let nextDay, title, label

    for (var i = 0; i < 28; i++) {
      nextDay = addDays(today, i)
      title = format(nextDay, 'EEEE, MMMM d')
      label = format(nextDay, 'M/d')
      if ( daysWithEvents.map(g => g.title).includes(title) ) {
        // push the original object in groups; not the
        next28Days.push(daysWithEvents.find(g => g.title === title))
      } else {
        // empty date; add to the next 30 days list
        next28Days.push({
          date: nextDay,
          title: title,
          label: {
            date: label,
            day: format(nextDay, 'E')
          },
          events: []
        })
      }
    }

    // this will really only ever run on initial data load
    // set the 'day' to the first day with events.
    // const firstDayWithEvents = next28Days.find(e => e.events.length)
    // firstDayWithEvents && setDay(firstDayWithEvents)

    // just set it to today
    setDay(next28Days[0])
    return next28Days
  }, [query.data])

  if (query.isLoading) {
    return null
  }

  // const firstDayWithEvents = days?.indexOf((day) => day.events?.length)

  return (
    <div>
      <div className="bg-gray-100 py-5 mb-10">
        <div className="container flex flex-col md:flex-row">
          <p className="mr-5 mb-5 md:mb-0 flex items-center font-platform font-bold text-gray-900 leading-tight">
            Food Trucks <br className="hidden md:block"/>serving on
          </p>
          <div className={["flex flex-1 overflow-hidden px-10"]}>
            <Slider
              slidesToShow={7}
              slidesToScroll={7}
              arrows={true}
              infinite={false}
              dots={false}
              variableWidth={false}
              centerMode={false}
              lazyLoad={false}
              className={[styles.slider, "w-full"]}
              responsive={[
                {
                  breakpoint: 1280,
                  settings: {
                    slidesToShow: 6,
                    slidesToScroll: 6,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 4,
                    slidesToScroll: 4,
                  },
                },
                {
                  breakpoint: 600,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 3,
                  },
                },
              ]}
            >
              {days.map((date, dateIndex) => (
                <div className={["py-3 flex items-center justify-center", styles.dateSliderCircleWrapper]} key={dateIndex}>
                  <div
                    key={dateIndex}
                    className={[
                      styles.dateSliderCircle,
                      "p-2 mr-2 rounded-full",
                      "flex items-center justify-center cursor-pointer",
                      date.label === day?.label ? "bg-blue text-white" : "bg-white text-blue",
                    ]}
                    onClick={() => {setDay(date)}}
                  >
                    <p className="leading-4 font-platform font-light text-center" style={{marginBottom:"4px"}}>
                      <span className="text-xs uppercase">{date?.label?.day}</span>
                      <br/>
                      {date?.label?.date}
                    </p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="">
          {day && (
            <div className="flex items-center">
              <Calendar size={25} />
              <div className="text-navy text-xl ml-3" style={{marginBottom: "3px"}}>
                {day?.title}
              </div>
            </div>
          )}

          {day?.events?.length ? (


            <div className="mt-5 pb-20 grid sm:grid-cols-2 md:grid-cols-3 gap-5 lg:gap-8">
              {day?.events?.map(event => {
                const caterer = event.caterer
                return (
                  <div
                    key={event.id}
                    className={[
                      'flex flex-col',
                      'bg-white rounded border border-transparent shadow col-span-1'
                    ]}
                  >
                    <div
                      className={'relative bg-cover bg-center bg-no-repeat rounded-t'}
                      style={{ backgroundImage: `url(${caterer.photo.url})`, minHeight: '130px' }}
                      width="100%"
                    >
                      <div className="absolute right-0 bg-green text-white rounded-sm text-xs p-1 m-2">
                        {format(event.start_date, 'h:mma')} - {format(event.end_date, 'h:mma')}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-row items-center leading-4">
                        <p className="flex-1 text-lg md:text-xl font-platform font-light text-navy">
                          {caterer.name}
                        </p>
                      </div>

                      <div className="flex-grow">
                        {caterer.tags.length > 0 && (
                          <div className="pt-4 flex flex-wrap -mb-1 leading-snug text-gray-700 font-light">
                            {caterer.tags.filter(t => !['savory', 'sweet'].includes(t.slug)).map(tag => (
                              <div key={tag.id} className="bg-gray-100 text-sm px-3 mr-1 mb-1 rounded-sm">
                                {tag.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="mt-10 pb-20 px-4 text-center italic text-gray-900">
              There are no scheduled events for this day (yet).
            </div>
          )}
          </div>
      </div>
    </div>
  )
}

const getCrumbs = (landmark, city) => {
  const crumbs = [{ text: 'Food Trucks', url: `/food-trucks/` }]

  if (city.region) {
    crumbs.push({
      text: city.region.name,
      url: `/food-trucks/${city.region.url_slug}/`
    })
  }

  if (city.parent) {
    crumbs.push({
      text: city.parent.name,
      url: `/food-trucks/${city.parent.url_slug}/`
    })
  }

  crumbs.push({
    text: city.name,
    url: `/food-trucks/${city.url_slug}/`
  })

  return crumbs.concat({
    text: landmark.name,
  })
}
